import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useReveal } from '../lib/useReveal';
import { cn } from '../lib/cn';

Modal.setAppElement('#root');

const modalClasses = {
  base: 'fixed inset-0 flex items-center justify-center p-4 sm:p-8 focus:outline-none outline-none',
  afterOpen: '',
  beforeClose: '',
};

const overlayClasses = {
  base: 'fixed inset-0 bg-ink/95 backdrop-blur-md z-50 opacity-0 transition-opacity duration-300',
  afterOpen: 'opacity-100',
  beforeClose: 'opacity-0',
};

export const Gallery = ({ data, language = 'bg' }) => {
  const headRef = useReveal();
  const gridRef = useReveal();

  const gallery = useMemo(() => data?.gallery || [], [data?.gallery]);
  const [selected, setSelected] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const touchStart = useRef(null);

  const count = gallery.length;

  const openAt = (i) => {
    setImgLoaded(false);
    setSelected(i);
  };

  const close = useCallback(() => setSelected(null), []);

  const goPrev = useCallback(() => {
    setImgLoaded(false);
    setSelected((s) => (s > 0 ? s - 1 : s));
  }, []);

  const goNext = useCallback(() => {
    setImgLoaded(false);
    setSelected((s) => (s !== null && s < count - 1 ? s + 1 : s));
  }, [count]);

  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, close, goPrev, goNext]);

  useEffect(() => {
    if (selected === null) return;
    [selected - 1, selected + 1].forEach((idx) => {
      if (gallery[idx]) {
        const img = new Image();
        img.src = gallery[idx].largeImage;
      }
    });
  }, [selected, gallery]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) delta < 0 ? goNext() : goPrev();
    touchStart.current = null;
  };

  const current = selected !== null ? gallery[selected] : null;

  return (
    <section id="galeriya" className="section-pad bg-paper text-ink relative">
      <div className="max-wrap section-wrap">
        <div ref={headRef} className="reveal grid grid-cols-12 gap-y-6 gap-x-6 items-end">
          <div className="col-span-12 lg:col-span-3">
            <span className="font-mono text-index text-ink-muted">03 / 04</span>
            <span className="block mt-4 h-px w-16 bg-ink/60" aria-hidden="true" />
          </div>
          <div className="col-span-12 lg:col-span-9">
            <h2 className="font-serif text-balance tracking-tightest leading-[0.98]"
                style={{ fontSize: 'clamp(2.25rem, 8vw, 6rem)' }}>
              {data?.title || (language === 'bg' ? 'Галерия' : 'Gallery')}
            </h2>
            {data?.desc && (
              <p className="mt-8 max-w-2xl text-ink-muted text-base sm:text-lg leading-relaxed text-pretty">
                {data.desc}
              </p>
            )}
          </div>
        </div>

        <ul
          ref={gridRef}
          className="reveal mt-14 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 lg:gap-x-8 gap-y-14 sm:gap-y-16"
        >
          {gallery.map((item, i) => (
            <li key={i} className="group">
              <button
                type="button"
                onClick={() => openAt(i)}
                className="block w-full text-left cursor-zoom-in"
                aria-label={item.title}
              >
                <figure>
                  <div className="relative overflow-hidden bg-paper-deep aspect-[4/5]">
                    <img
                      src={item.smallImage}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out-quart group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-ink/0 group-hover:ring-ink/10 transition-[box-shadow] duration-500" />
                  </div>
                  <figcaption className="mt-5 flex items-baseline justify-between gap-4">
                    <span className="font-serif text-lg sm:text-xl tracking-editorial text-ink text-pretty">
                      {item.title}
                    </span>
                    <span className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-ink-faint whitespace-nowrap tabular-nums">
                      {String(i + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                    </span>
                  </figcaption>
                </figure>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <Modal
        isOpen={selected !== null}
        onRequestClose={close}
        className={modalClasses}
        overlayClassName={overlayClasses}
        closeTimeoutMS={280}
        shouldCloseOnOverlayClick
        contentLabel={current?.title || (language === 'bg' ? 'Галерия' : 'Gallery')}
      >
        {current && (
          <div
            className="relative w-full h-full flex flex-col text-paper"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="flex items-start justify-between gap-4 shrink-0">
              <div className="flex flex-col max-w-[78%]">
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-paper/60 tabular-nums">
                  {String(selected + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                </span>
                <span className="font-serif text-lg sm:text-2xl tracking-editorial mt-1 text-pretty">
                  {current.title}
                </span>
              </div>
              <button
                onClick={close}
                className="w-10 h-10 shrink-0 flex items-center justify-center text-paper/80 hover:text-paper hover:bg-paper/10 rounded-full transition-colors duration-300"
                aria-label="Close"
              >
                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" aria-hidden="true">
                  <path stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="relative flex-1 flex items-center justify-center my-4 min-h-0">
              {!imgLoaded && (
                <span className="absolute w-8 h-8 border border-paper/20 border-t-paper rounded-full animate-spin" />
              )}
              <img
                key={current.largeImage}
                src={current.largeImage}
                alt={current.title}
                onLoad={() => setImgLoaded(true)}
                className={cn(
                  'max-w-full max-h-full object-contain transition-opacity duration-500',
                  imgLoaded ? 'opacity-100' : 'opacity-0'
                )}
              />

              <button
                onClick={goPrev}
                disabled={selected === 0}
                className="absolute left-0 sm:left-2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 rounded-full transition-colors duration-300 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-paper/70"
                aria-label="Previous image"
              >
                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" aria-hidden="true">
                  <path stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" d="M15 5l-7 7 7 7" />
                </svg>
              </button>
              <button
                onClick={goNext}
                disabled={selected === count - 1}
                className="absolute right-0 sm:right-2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 rounded-full transition-colors duration-300 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-paper/70"
                aria-label="Next image"
              >
                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" aria-hidden="true">
                  <path stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="shrink-0 flex items-center justify-center gap-1.5 pb-1">
              {gallery.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setImgLoaded(false); setSelected(i); }}
                  className={cn(
                    'h-1 transition-all duration-300 rounded-full',
                    i === selected ? 'w-8 bg-paper' : 'w-3 bg-paper/30 hover:bg-paper/60'
                  )}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};
